import { z } from "zod";

export const ranks = [
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "T",
  "J",
  "Q",
  "K",
  "A",
  "2",
] as const;

export const suits = ["d", "h", "s", "c"] as const;

export const RankSchema = z.enum(ranks);
export const SuitSchema = z.enum(suits);
export const CardSchema = z
  .string()
  .regex(/^[3-9TJQKA2][dhsc]$/)
  .transform((value) => value as Card);

export type Rank = (typeof ranks)[number];
export type Suit = (typeof suits)[number];
export type Card = `${Rank}${Suit}`;
export type GameStatus = "waiting" | "ongoing" | "ended";

export interface PlayerState {
  id: string;
  name: string;
  seat: number;
  tokenHash: string;
  hand: Card[];
  joinedAt: number;
}

export interface TablePlay {
  playerId: string;
  cards: Card[];
  playedAt: number;
  description: string;
}

export interface GameState {
  roomCode: string;
  status: GameStatus;
  hostId: string;
  players: PlayerState[];
  activePlayerId: string | null;
  startingCard: Card | null;
  firstMovePending: boolean;
  lastPlay: TablePlay | null;
  passes: string[];
  winnerId: string | null;
  moveNumber: number;
  revision: number;
  createdAt: number;
  updatedAt: number;
}

export interface PublicPlayer {
  id: string;
  name: string;
  seat: number;
  cardCount: number;
  isHost: boolean;
  isConnected: boolean;
}

export interface PlayerGameView {
  roomCode: string;
  status: GameStatus;
  hostId: string;
  yourId: string;
  isHost: boolean;
  players: PublicPlayer[];
  hand: Card[];
  activePlayerId: string | null;
  startingCard: Card | null;
  firstMovePending: boolean;
  lastPlay: TablePlay | null;
  winnerId: string | null;
  moveNumber: number;
  revision: number;
  canStart: boolean;
}

export interface RoomCredential {
  roomCode: string;
  playerId: string;
  playerName: string;
  token: string;
}

export interface RoomError {
  code: string;
  message: string;
  status: number;
}

export type RoomResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: RoomError };

const PlayerStateSchema = z.object({
  id: z.string(),
  name: z.string(),
  seat: z.number().int().min(0).max(3),
  tokenHash: z.string(),
  hand: z.array(CardSchema),
  joinedAt: z.number(),
});

export const TablePlaySchema = z.object({
  playerId: z.string(),
  cards: z.array(CardSchema),
  playedAt: z.number(),
  description: z.string(),
});

export const GameStateSchema: z.ZodType<GameState> = z.object({
  roomCode: z.string(),
  status: z.enum(["waiting", "ongoing", "ended"]),
  hostId: z.string(),
  players: z.array(PlayerStateSchema),
  activePlayerId: z.string().nullable(),
  startingCard: CardSchema.nullable(),
  firstMovePending: z.boolean(),
  lastPlay: TablePlaySchema.nullable(),
  passes: z.array(z.string()),
  winnerId: z.string().nullable(),
  moveNumber: z.number().int().nonnegative(),
  revision: z.number().int().nonnegative(),
  createdAt: z.number(),
  updatedAt: z.number(),
});

export const PublicPlayerSchema = z.object({
  id: z.string(),
  name: z.string(),
  seat: z.number().int().min(0).max(3),
  cardCount: z.number().int().nonnegative(),
  isHost: z.boolean(),
  isConnected: z.boolean(),
});

export const PlayerGameViewSchema: z.ZodType<PlayerGameView> = z.object({
  roomCode: z.string(),
  status: z.enum(["waiting", "ongoing", "ended"]),
  hostId: z.string(),
  yourId: z.string(),
  isHost: z.boolean(),
  players: z.array(PublicPlayerSchema),
  hand: z.array(CardSchema),
  activePlayerId: z.string().nullable(),
  startingCard: CardSchema.nullable(),
  firstMovePending: z.boolean(),
  lastPlay: TablePlaySchema.nullable(),
  winnerId: z.string().nullable(),
  moveNumber: z.number().int().nonnegative(),
  revision: z.number().int().nonnegative(),
  canStart: z.boolean(),
});

export const RoomCredentialSchema: z.ZodType<RoomCredential> = z.object({
  roomCode: z.string(),
  playerId: z.string(),
  playerName: z.string(),
  token: z.string(),
});
